import { FC, useEffect, useMemo, useState } from 'react';
import { NotificationManager } from 'react-notifications';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import AppLayout from '../../components/layout/app-layout/app-layout';
import DataTable from '../../components/shared/DataTable/DataTable';
import Tabs from '../../components/shared/Tabs';
import BtButton from '../bt-button/bt-button';
import Modal from '../../components/shared/Modal/Modal';
import Field from '../../components/shared/Field/Field';
import serviceApiClients, { ApiClient, CreateClientResponse } from '../../services/service-api-clients';
import { formatDate } from '../../util';
import styles from './page-api-clients.module.scss';

// Create a separate axios instance for API testing that bypasses interceptors
const apiTestAxios = axios.create();
type ApiResult = {
  loading: boolean;
  data: any;
  error: string | null;
};

const PageApiClients: FC = () => {
  const [t] = useTranslation('site', { keyPrefix: 'apiClients' });
  const [clients, setClients] = useState<ApiClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientDescription, setNewClientDescription] = useState('');
  const [createdCredentials, setCreatedCredentials] = useState<CreateClientResponse['credentials'] | null>(null);
  const [saving, setSaving] = useState(false);

  // API Test state
  const [testClientId, setTestClientId] = useState('');
  const [testClientSecret, setTestClientSecret] = useState('');
  const [testOrgNr, setTestOrgNr] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testPublishedCategories, setTestPublishedCategories] = useState('');
  const [testPublishedPlaces, setTestPublishedPlaces] = useState('');
  const [testNextProjectAuthToken, setTestNextProjectAuthToken] = useState('');

  const [unansweredResult, setUnansweredResult] = useState<ApiResult>({ loading: false, data: null, error: null });
  const [publishedResult, setPublishedResult] = useState<ApiResult>({ loading: false, data: null, error: null });
  const [messagesResult, setMessagesResult] = useState<ApiResult>({ loading: false, data: null, error: null });
  const [organizationResult, setOrganizationResult] = useState<ApiResult>({ loading: false, data: null, error: null });
  const [categoriesResult, setCategoriesResult] = useState<ApiResult>({ loading: false, data: null, error: null });
  const [placesResult, setPlacesResult] = useState<ApiResult>({ loading: false, data: null, error: null });
  const [nextProjectPostResult, setNextProjectPostResult] = useState<ApiResult>({ loading: false, data: null, error: null });
  const [nextProjectDeleteResult, setNextProjectDeleteResult] = useState<ApiResult>({ loading: false, data: null, error: null });
  const [notificationsResult, setNotificationsResult] = useState<ApiResult>({ loading: false, data: null, error: null });
  const [testNotificationsLang, setTestNotificationsLang] = useState('');
  const [testNotificationsCategories, setTestNotificationsCategories] = useState('');
  const [testNotificationsPlaces, setTestNotificationsPlaces] = useState('');

  const fetchClients = async () => {
    setLoading(true);
    try {
      const data = await serviceApiClients.listClients();
      setClients(data.filter(client => client.isActive));
    } catch (error) {
      NotificationManager.error(t('notification.fetchError', 'Kunde inte hämta API-klienter'));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleCreateClient = async () => {
    if (!newClientName.trim()) {
      NotificationManager.error(t('notification.nameRequired', 'Namn krävs'));
      return;
    }

    setSaving(true);
    try {
      const response = await serviceApiClients.createClient({
        name: newClientName.trim(),
        description: newClientDescription.trim() || undefined,
      });
      
      setCreatedCredentials(response.credentials);
      setShowAddModal(false);
      setShowCredentialsModal(true);
      setNewClientName('');
      setNewClientDescription('');
      await fetchClients();
      NotificationManager.success(t('notification.createSuccess', 'API-klient skapad'));
    } catch (error) {
      NotificationManager.error(t('notification.createError', 'Kunde inte skapa API-klient'));
    }
    setSaving(false);
  };

  const handleDeleteClient = async (client: ApiClient) => {
    if (!window.confirm(t('deleteConfirm', 'Är du säker på att du vill ta bort "{{name}}"? Detta kan inte ångras.', { name: client.name }))) {
      return;
    }

    try {
      await serviceApiClients.deleteClient(client.id);
      await fetchClients();
      NotificationManager.success(t('notification.deleteSuccess', 'API-klient borttagen'));
    } catch (error) {
      NotificationManager.error(t('notification.deleteError', 'Kunde inte ta bort API-klient'));
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    NotificationManager.success(t('notification.copied', '{{label}} kopierad till urklipp', { label }));
  };

  const authHeaders = {
    'X-Client-ID': testClientId,
    'X-Client-Secret': testClientSecret,
  };

  // API Test functions
  const callApi = async (
    endpoint: string,
    params: Record<string, string>,
    setResult: (result: ApiResult) => void
  ) => {
    if (!testClientId || !testClientSecret) {
      NotificationManager.error(t('test.missingCredentials', 'Ange Client ID och Client Secret'));
      return;
    }

    setResult({ loading: true, data: null, error: null });

    try {
      const response = await apiTestAxios.get(`/api/v1${endpoint}`, {
        params,
        headers: authHeaders,
      });
      setResult({ loading: false, data: response.data, error: null });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      const data = error.response?.data?.link ? { link: error.response?.data?.link } : null;
      setResult({ loading: false, data: data, error: errorMessage });
    }
  };

  const callApiWithMethod = async (
    method: 'get' | 'post' | 'delete',
    endpoint: string,
    setResult: (result: ApiResult) => void,
    body?: object
  ) => {
    if (!testClientId || !testClientSecret) {
      NotificationManager.error(t('test.missingCredentials', 'Ange Client ID och Client Secret'));
      return;
    }

    setResult({ loading: true, data: null, error: null });

    try {
      const config = { headers: authHeaders };
      const response =
        method === 'get'
          ? await apiTestAxios.get(`/api/v1${endpoint}`, config)
          : method === 'post'
            ? await apiTestAxios.post(`/api/v1${endpoint}`, body ?? {}, { ...config, headers: { ...config.headers, 'Content-Type': 'application/json' } })
            : await apiTestAxios.delete(`/api/v1${endpoint}`, config);
      setResult({ loading: false, data: response.data, error: null });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      setResult({ loading: false, data: null, error: errorMessage });
    }
  };

  const testUnanswered = () => {
    if (!testOrgNr || !testEmail) {
      NotificationManager.error(t('test.missingParams', 'Ange orgNr och email'));
      return;
    }
    callApi('/bid-requests/unanswered/count', { orgNr: testOrgNr, email: testEmail }, setUnansweredResult);
  };

  const testPublished = () => {
    if (!testOrgNr) {
      NotificationManager.error(t('test.missingOrgNr', 'Ange orgNr'));
      return;
    }
    const params: Record<string, string> = { orgNr: testOrgNr };
    if (testPublishedCategories.trim()) params.categories = testPublishedCategories.trim();
    if (testPublishedPlaces.trim()) params.places = testPublishedPlaces.trim();
    callApi('/bid-requests/published/matching/count', params, setPublishedResult);
  };

  const testMessages = () => {
    if (!testOrgNr || !testEmail) {
      NotificationManager.error(t('test.missingParams', 'Ange orgNr och email'));
      return;
    }
    callApi('/messages/unread/count', { orgNr: testOrgNr, email: testEmail }, setMessagesResult);
  };

  const testOrganization = () => {
    if (!testOrgNr) {
      NotificationManager.error(t('test.missingOrgNr', 'Ange orgNr'));
      return;
    }
    callApi(`/organization/${encodeURIComponent(testOrgNr)}`, {}, setOrganizationResult);
  };

  const testCategories = () => {
    callApi('/categories', {}, setCategoriesResult);
  };

  const testPlaces = () => {
    callApi('/places', {}, setPlacesResult);
  };

  const testNotifications = () => {
    if (!testOrgNr) {
      NotificationManager.error(t('test.missingOrgNr', 'Ange orgNr'));
      return;
    }
    const params: Record<string, string> = { orgNr: testOrgNr };
    if (testEmail.trim()) params.email = testEmail.trim();
    if (testNotificationsLang.trim()) params.lang = testNotificationsLang.trim();
    if (testNotificationsCategories.trim()) params.categories = testNotificationsCategories.trim();
    if (testNotificationsPlaces.trim()) params.places = testNotificationsPlaces.trim();
    callApi('/notifications', params, setNotificationsResult);
  };

  const testNextProjectPost = () => {
    if (!testOrgNr) {
      NotificationManager.error(t('test.missingOrgNr', 'Ange orgNr'));
      return;
    }
    if (!testNextProjectAuthToken.trim()) {
      NotificationManager.error(t('test.nextProject.missingAuthToken', 'Ange authToken'));
      return;
    }
    callApiWithMethod(
      'post',
      `/organization/${encodeURIComponent(testOrgNr)}/integrations/next-project`,
      setNextProjectPostResult,
      { authToken: testNextProjectAuthToken.trim() }
    );
  };

  const testNextProjectDelete = () => {
    if (!testOrgNr) {
      NotificationManager.error(t('test.missingOrgNr', 'Ange orgNr'));
      return;
    }
    callApiWithMethod(
      'delete',
      `/organization/${encodeURIComponent(testOrgNr)}/integrations/next-project`,
      setNextProjectDeleteResult
    );
  };

  const columns = useMemo(() => [
    {
      title: t('table.name', 'Namn'),
      field: 'name',
    },
    {
      title: t('table.clientId', 'Klient ID'),
      field: 'clientId',
    },
    {
      title: t('table.description', 'Beskrivning'),
      field: 'description',
      render: (row: ApiClient) => row.description || '-',
    },
    {
      title: t('table.createdAt', 'Skapad'),
      field: 'createdAt',
      render: (row: ApiClient) => formatDate(row.createdAt),
    },
    {
      title: t('table.lastUsedAt', 'Senast använd'),
      field: 'lastUsedAt',
      render: (row: ApiClient) => row.lastUsedAt ? formatDate(row.lastUsedAt) : '-',
    },
    {
      title: '',
      render: (row: ApiClient) => (
        <BtButton size="small" color="white" onClick={() => handleDeleteClient(row)}>
          {t('button.delete', 'Ta bort')}
        </BtButton>
      ),
    },
  ], [t]);

  const renderResultBox = (result: ApiResult, title: string) => (
    <div className={styles.resultBox}>
      <div className={styles.resultTitle}>{title}</div>
      {result.loading && <div className={styles.resultLoading}>{t('test.loading', 'Laddar...')}</div>}
      {result.error && <div className={styles.resultError}>{result.error}</div>}
      {result.data && (
        <pre className={styles.resultData}>{JSON.stringify(result.data, null, 2)}</pre>
      )}
      {!result.loading && !result.error && !result.data && (
        <div className={styles.resultEmpty}>{t('test.noResult', 'Ingen data än')}</div>
      )}
    </div>
  );

  return (
    <AppLayout title={t('pageTitle', 'API-klienter')}>
      <div style={{ margin: '32px' }}>
        <div className="tab-container">
          <Tabs
            tabs={[t('tabs.all', 'Alla'), t('tabs.requests', 'Testa API')]}
            selected={selectedTab}
            onChange={tab => setSelectedTab(tab)}
          />
          
          {selectedTab === 0 && (
            <div className="tab-actions">
              <BtButton onClick={() => setShowAddModal(true)}>
                {t('button.addClient', 'Lägg till klient')}
              </BtButton>
            </div>
          )}
        </div>
        
        {selectedTab === 0 && (
          <>
            <DataTable
              columns={columns}
              data={clients}
              isLoading={loading}
            />

            {/* Add Client Modal */}
            <Modal
              show={showAddModal}
              setShow={setShowAddModal}
              title={t('modal.add.title', 'Lägg till API-klient')}
              buttonInfo={{
                label: t('button.create', 'Skapa'),
                action: handleCreateClient,
                loaderShow: saving,
              }}
            >
              <Field
                label={t('modal.add.nameLabel', 'Namn *')}
                value={newClientName}
                onChange={setNewClientName}
              />
              <Field
                label={t('modal.add.descriptionLabel', 'Beskrivning')}
                value={newClientDescription}
                onChange={setNewClientDescription}
              />
            </Modal>

            {/* Credentials Modal */}
            <Modal
              show={showCredentialsModal}
              setShow={(value) => {
                setShowCredentialsModal(value);
                if (!value) setCreatedCredentials(null);
              }}
              title={t('modal.credentials.title', 'API-uppgifter')}
              hasCloseButton
              buttonInfo={{
                label: t('button.savedCredentials', 'Jag har sparat uppgifterna'),
                action: () => setShowCredentialsModal(false),
              }}
            >
              <div className={styles.warning}>
                ⚠️ <strong>{t('modal.credentials.importantLabel', 'Viktigt!')}</strong> {t('modal.credentials.warning', 'Spara dessa uppgifter nu. Client Secret kan inte visas igen efter att du stänger detta fönster.')}
              </div>
              
              {createdCredentials && (
                <div className={styles.credentials}>
                  <div className={styles.credentialField}>
                    <label>{t('modal.credentials.clientIdLabel', 'Client ID')}</label>
                    <div className={styles.credentialValue}>
                      <code>{createdCredentials.clientId}</code>
                      <BtButton 
                        size="small" 
                        color="white"
                        onClick={() => copyToClipboard(createdCredentials.clientId, 'Client ID')}
                      >
                        {t('button.copy', 'Kopiera')}
                      </BtButton>
                    </div>
                  </div>
                  <div className={styles.credentialField}>
                    <label>{t('modal.credentials.clientSecretLabel', 'Client Secret')}</label>
                    <div className={styles.credentialValue}>
                      <code className={styles.secret}>{createdCredentials.clientSecret}</code>
                      <BtButton 
                        size="small"
                        color="white"
                        onClick={() => copyToClipboard(createdCredentials.clientSecret, 'Client Secret')}
                      >
                        {t('button.copy', 'Kopiera')}
                      </BtButton>
                    </div>
                  </div>
                </div>
              )}
            </Modal>
          </>
        )}

        {selectedTab === 1 && (
          <div className={styles.testContainer}>
            <div className={styles.testSection}>
              <h3>{t('test.credentials', 'API-uppgifter')}</h3>
              <div className={styles.testFields}>
                <Field
                  label="Client ID"
                  value={testClientId}
                  onChange={setTestClientId}
                />
                <Field
                  label="Client Secret"
                  value={testClientSecret}
                  onChange={setTestClientSecret}
                />
              </div>
            </div>

            <div className={styles.testSection}>
              <h3>{t('test.parameters', 'Parametrar')}</h3>
              <div className={styles.testFields}>
                <Field
                  label="orgNr"
                  value={testOrgNr}
                  onChange={setTestOrgNr}
                />
                <Field
                  label="email"
                  value={testEmail}
                  onChange={setTestEmail}
                />
              </div>
            </div>

            <div className={styles.testSection}>
              <h3>{t('test.endpoints', 'Endpoints')}</h3>

              <div className={styles.endpointCard}>
                <div className={styles.endpointRow}>
                  <div className={styles.endpointInfo}>
                    <code>/bid-requests/unanswered/count</code>
                    <span className={styles.endpointDesc}>{t('test.unansweredDesc', 'Antal obesvarade anbudsförfrågningar')}</span>
                  </div>
                  <BtButton size="small" onClick={testUnanswered} loaderShow={unansweredResult.loading}>
                    {t('test.run', 'Kör')}
                  </BtButton>
                </div>
                {renderResultBox(unansweredResult, 'Result')}
              </div>

              <div className={styles.endpointCard}>
                <div className={styles.endpointBlock}>
                  <div className={styles.endpointRow}>
                    <div className={styles.endpointInfo}>
                      <code>/bid-requests/published/matching/count</code>
                      <span className={styles.endpointDesc}>{t('test.publishedDesc', 'Antal matchande publika förfrågningar')}</span>
                    </div>
                    <BtButton size="small" onClick={testPublished} loaderShow={publishedResult.loading}>
                      {t('test.run', 'Kör')}
                    </BtButton>
                  </div>
                  <div className={styles.endpointParams}>
                    <Field
                      label="categories (optional)"
                      value={testPublishedCategories}
                      onChange={setTestPublishedCategories}
                      placeholder="e.g. cat1, cat2"
                    />
                    <Field
                      label="places (optional)"
                      value={testPublishedPlaces}
                      onChange={setTestPublishedPlaces}
                      placeholder="e.g. place1, place2"
                    />
                  </div>
                </div>
                {renderResultBox(publishedResult, 'Result')}
              </div>

              <div className={styles.endpointCard}>
                <div className={styles.endpointRow}>
                  <div className={styles.endpointInfo}>
                    <code>/messages/unread/count</code>
                    <span className={styles.endpointDesc}>{t('test.messagesDesc', 'Antal olästa meddelanden')}</span>
                  </div>
                  <BtButton size="small" onClick={testMessages} loaderShow={messagesResult.loading}>
                    {t('test.run', 'Kör')}
                  </BtButton>
                </div>
                {renderResultBox(messagesResult, 'Result')}
              </div>

              <div className={styles.endpointCard}>
                <div className={styles.endpointBlock}>
                  <div className={styles.endpointRow}>
                    <div className={styles.endpointInfo}>
                      <code>/notifications</code>
                      <span className={styles.endpointDesc}>{t('test.notificationsDesc', 'Kombinerade notifieringar (obesvarade, matchande, olästa)')}</span>
                    </div>
                    <BtButton size="small" onClick={testNotifications} loaderShow={notificationsResult.loading}>
                      {t('test.run', 'Kör')}
                    </BtButton>
                  </div>
                  <div className={styles.endpointParams}>
                    <Field
                      label="lang (optional)"
                      value={testNotificationsLang}
                      onChange={setTestNotificationsLang}
                      placeholder="sv, en, nb, da"
                    />
                    <Field
                      label="categories (optional)"
                      value={testNotificationsCategories}
                      onChange={setTestNotificationsCategories}
                      placeholder="e.g. cat1, cat2"
                    />
                    <Field
                      label="places (optional)"
                      value={testNotificationsPlaces}
                      onChange={setTestNotificationsPlaces}
                      placeholder="e.g. Stockholm|Göteborg"
                    />
                  </div>
                </div>
                {renderResultBox(notificationsResult, 'Result')}
              </div>

              <div className={styles.endpointCard}>
                <div className={styles.endpointRow}>
                  <div className={styles.endpointInfo}>
                    <code>/organization/{'{orgNr}'}</code>
                    <span className={styles.endpointDesc}>{t('test.organizationDesc', 'Organisationsinfo och integrationer')}</span>
                  </div>
                  <BtButton size="small" onClick={testOrganization} loaderShow={organizationResult.loading}>
                    {t('test.run', 'Kör')}
                  </BtButton>
                </div>
                {renderResultBox(organizationResult, 'Result')}
              </div>

              <div className={styles.endpointCard}>
                <div className={styles.endpointRow}>
                  <div className={styles.endpointInfo}>
                    <code>/categories</code>
                    <span className={styles.endpointDesc}>{t('test.categoriesDesc', 'Lista yrkeskategorier för filter')}</span>
                  </div>
                  <BtButton size="small" onClick={testCategories} loaderShow={categoriesResult.loading}>
                    {t('test.run', 'Kör')}
                  </BtButton>
                </div>
                {renderResultBox(categoriesResult, 'Result')}
              </div>

              <div className={styles.endpointCard}>
                <div className={styles.endpointRow}>
                  <div className={styles.endpointInfo}>
                    <code>/places</code>
                    <span className={styles.endpointDesc}>{t('test.placesDesc', 'Lista platser för filter')}</span>
                  </div>
                  <BtButton size="small" onClick={testPlaces} loaderShow={placesResult.loading}>
                    {t('test.run', 'Kör')}
                  </BtButton>
                </div>
                {renderResultBox(placesResult, 'Result')}
              </div>

              <div className={styles.endpointCard}>
                <div className={styles.endpointBlock}>
                  <div className={styles.endpointRow}>
                    <div className={styles.endpointInfo}>
                      <code>POST /organization/{'{orgNr}'}/integrations/next-project</code>
                      <span className={styles.endpointDesc}>{t('test.nextProject.postDesc', 'Spara Next Project-integration (authToken)')}</span>
                    </div>
                    <BtButton size="small" onClick={testNextProjectPost} loaderShow={nextProjectPostResult.loading}>
                      {t('test.run', 'Kör')}
                    </BtButton>
                  </div>
                  <div className={styles.endpointParams}>
                    <Field
                      label="authToken (required)"
                      value={testNextProjectAuthToken}
                      onChange={setTestNextProjectAuthToken}
                      placeholder="Next Project auth token"
                    />
                  </div>
                </div>
                {renderResultBox(nextProjectPostResult, 'Result')}
              </div>

              <div className={styles.endpointCard}>
                <div className={styles.endpointRow}>
                  <div className={styles.endpointInfo}>
                    <code>DELETE /organization/{'{orgNr}'}/integrations/next-project</code>
                    <span className={styles.endpointDesc}>{t('test.nextProject.deleteDesc', 'Ta bort Next Project-integration')}</span>
                  </div>
                  <BtButton size="small" onClick={testNextProjectDelete} loaderShow={nextProjectDeleteResult.loading}>
                    {t('test.run', 'Kör')}
                  </BtButton>
                </div>
                {renderResultBox(nextProjectDeleteResult, 'Result')}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default PageApiClients;
